'use strict';

export default angular
  .module('maestro.services.data.bots', [
    'maestro.services.webcore'
  ])
  .service('BotsService', BotsService)
  .name;

function BotsService(
  webcore,
  $q,
  CacheService,
  I18nService
) {

  /**
   * Search for available Bots
   * @param params
   * @returns {*}
   */
  this.searchBots = (params) => {
    if (!params.lang) {
      params.lang = 'en'; // Set English by default
    }
    // params.compatible = true;
    params.sortCollection = 'apps';

    return $q.when(
      webcore.services.bots.searchBots(params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Search Bots available for specific Organization.
   * @param organizationId
   * @param params
   * @returns {*}
   */
  this.searchOrganizationBots = (organizationId, params) => {
    if (!organizationId || organizationId < 1 || isNaN(organizationId)) {
      return $q.reject('Organization ID is not specified!');
    }
    if (!params) {
      params = {};
    }
    params.organizationId = organizationId;

    return this.searchBots(params);
  };

  /**
   * Search Bots available for specific Location.
   * @param locationId
   * @param params
   * @returns {*}
   */
  this.searchLocationBots = (locationId, params) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }
    if (!params) {
      params = {};
    }
    params.locationId = locationId;

    return this.searchBots(params);
  };

  /**
   * Returns detailed information of specific Bot.
   * @param params Request parameters.
   * @param {string} params.bundle Globally unique bundle ID for the app
   * @param {string} [params.lang] Language identifier, default is user's language or 'en'
   * @param {string} [params.lastNVersions] Max number of versions to show, default is 10
   * @returns {Promise<GetBotInfoApiResponse>}
   */
  this.getBotInfo = (params) => {
    params = params || {};
    if (!params.lang) {
      params.lang = I18nService.getLocale()?.code || 'en'; // Set English by default
    }

    return $q.when(
      webcore.services.bots.getBotInfo(params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Purchase Bot at specified Organization
   * @param organizationId Target Organization ID
   * @param bundle Bot to purchase
   * @returns {*}
   */
  this.purchaseOrganizationBot = (organizationId, bundle) => {
    if (!organizationId || organizationId < 1 || isNaN(organizationId)) {
      return $q.reject('Organization ID is not specified!');
    }

    return $q.when(
      webcore.services.bots.purchaseBotInstance(bundle, organizationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Purchase Bot at specified Location
   * @param locationId Target Location ID
   * @param bundle Bot to purchase
   * @returns {*}
   */
  this.purchaseLocationBot = (locationId, bundle) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Organization ID is not specified!');
    }

    return $q.when(
      webcore.services.bots.purchaseBotInstance(bundle, void 0, locationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Get list of Bot Instances at specified Organization.
   * @param organizationId
   * @returns {*}
   */
  this.getOrganizationBots = (organizationId) => {
    if (!organizationId || organizationId < 1 || isNaN(organizationId)) {
      return $q.reject('Organization ID is not specified!');
    }

    return $q.when(
      webcore.services.bots.getListOfBotInstances(void 0, organizationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Get list of Bot Instances at specified Location.
   * @param locationId
   * @returns {*}
   */
  this.getLocationBots = (locationId) => {
    if (!locationId || locationId < 1 || isNaN(locationId)) {
      return $q.reject('Location ID is not specified!');
    }

    return $q.when(
      webcore.services.bots.getListOfBotInstances(void 0, void 0, locationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Get bot instance details by ID.
   * @param appInstanceId
   * @param organizationId
   * @param locationId
   * @returns {*}
   */
  this.getBotInstance = (appInstanceId, organizationId, locationId) => {
    if (!appInstanceId || appInstanceId < 1 || isNaN(appInstanceId)) {
      return $q.reject('Bot Instance ID is not specified!');
    }
    if (!organizationId && !locationId) {
      return $q.reject('Missed Location ID or Organization ID!');
    }
    organizationId = organizationId || void 0;
    locationId = locationId || void 0;

    return $q.when(
      webcore.services.bots.getListOfBotInstances(appInstanceId, organizationId, locationId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Set new status for bot instance.
   * @param appInstanceId
   * @param status
   * @returns {*}
   */
  this.setBotStatus = (appInstanceId, status) => {
    if (!appInstanceId || appInstanceId < 1 || isNaN(appInstanceId)) {
      return $q.reject('Bot Instance ID is not specified!');
    }
    if (!status || status < 1 || isNaN(status)) {
      return $q.reject('Status is not specified!');
    }

    return $q.when(
      webcore.services.bots.configureBotInstance(appInstanceId, status)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Remove bot instance by ID.
   * @param appInstanceId
   * @returns {*}
   */
  this.deleteBotInstance = (appInstanceId) => {
    if (!appInstanceId || appInstanceId < 1 || isNaN(appInstanceId)) {
      return $q.reject('Bot Instance ID is not specified!');
    }

    return $q.when(
      webcore.services.bots.deleteBotInstance(appInstanceId)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  /**
   * Get bot summary.
   * @param locationId
   * @param organizationId
   * @returns {*}
   */
  this.getBotSummary = CacheService.cachePromiseFunc((locationId, organizationId) => {
    if (!organizationId && !locationId) {
      return $q.reject('Missed Location ID or Organization ID!');
    }

    return $q.when(
      webcore.services.bots.getBotSummary(locationId || void 0, organizationId || void 0)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  }, 30000);

  /**
   * Send data-stream message.
   * See {@link https://iotbots.docs.apiary.io/#reference/end-user-bot-shop-apis/data-stream/send-message}
   *
   * The message can be sent to bots subscribed on the specific data stream address or bots of provided instance ID's.
   *
   * @param {{ locations?: number[], bots?: number[], feed?: Object }} message Data stream message
   * @param params Request parameters.
   * @param {number} params.scope Bitmask to feed organizational (`2`), individual (`1`) and circle(`4`) bots.
   * @param {string} params.address Data stream address.
   * @param {number} [params.locationId] Send data to bots of this location. Mandatory for users.
   * @param {number} [params.organizationId] Send data to bots of this organization, used by an administrators.
   * @returns {Promise}
   */
  this.sendDataStreamMessage = (message, params) => {
    return $q.when(
      webcore.services.bots.sendDataStreamMessage(message, params)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  };

  // #region Bot Notification Settings

  /**
   * Get bot notification settings.
   * See {@link https://sboxall.peoplepowerco.com/cloud/apidocs/bots.html#tag/End-User-Bot-Shop-APIs/operation/Get%20Bot%20Notifications}
   *
   * @param {number} organizationId Organization ID
   * @param {string} bundle Bot bundle ID
   * @returns {Promise}
   */
  this.getBotNotificationSettings = (organizationId, bundle) => {
    if (!organizationId || organizationId < 1 || isNaN(organizationId)) {
      return $q.reject('Organization ID is not specified!');
    }

    return $q.when(
      webcore.services.bots.getBotNotifications(organizationId, bundle)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  }

  /**
   * Update bot notification settings.
   * See {@link https://sboxall.peoplepowerco.com/cloud/apidocs/bots.html#tag/End-User-Bot-Shop-APIs/operation/Update%20Bot%20Notifications}
   *
   * @param {number} organizationId Organization ID
   * @param {string} bundle Bot bundle ID
   * @param {Object} settings Notification settings to update
   * @returns {Promise}
   */
  this.updateBotNotificationSettings = (organizationId, bundle, settings) => {
    if (!organizationId || organizationId < 1 || isNaN(organizationId)) {
      return $q.reject('Organization ID is not specified!');
    }
    if (!bundle) {
      return $q.reject('Bot bundle is not specified!');
    }

    return $q.when(
      webcore.services.bots.updateBotNotifications(organizationId, bundle, settings)
        .then(data => $q.resolve(data), err => $q.reject(err))
    );
  }

  // #endregion

}
